/* global google */
import { reactive } from 'vue'
import pin2d8cf0 from '/@/assets/icons/pin-2d8cf0.svg'
import { MapDoodleType } from '/@/constants/map'
import { getRoot } from '/@/root'
import { MapDoodleEnum } from '/@/types/map-enum'
import { EFlightAreaType } from '../types/flight-area'
import { message } from 'ant-design-vue'

export function useMouseTool () {
  const root = getRoot()

  const state = reactive({
    pinNum: 0,
    polylineNum: 0,
    polygonNum: 0,
    currentType: '',
  })

  const flightAreaColorMap = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }

  // Helper to clear previous listeners
  function clearDrawingListeners () {
    google.maps.event.clearListeners(root.$drawing, 'overlaycomplete')
  }

  function drawPin (type: MapDoodleType, getDrawCallback: Function) {
    root.$drawing.setDrawingMode(google.maps.drawing.OverlayType.MARKER)
    clearDrawingListeners()

    google.maps.event.addListener(root.$drawing, 'overlaycomplete', (event: any) => {
      const marker = event.overlay as google.maps.Marker
      marker.setTitle(type + state.pinNum)
      state.pinNum++
      getDrawCallback(marker)
    })
  }

  function drawPolyline (type: MapDoodleType, getDrawCallback: Function) {
    root.$drawing.setDrawingMode(google.maps.drawing.OverlayType.POLYLINE)
    clearDrawingListeners()

    google.maps.event.addListener(root.$drawing, 'overlaycomplete', (event: any) => {
      const polyline = event.overlay as google.maps.Polyline
      polyline.setOptions({
        strokeColor: '#2d8cf0',
        strokeOpacity: 1,
        strokeWeight: 2,
      })
      getDrawCallback(polyline)
    })
  }

  function drawPolygon (type: MapDoodleType, getDrawCallback: Function) {
    root.$drawing.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
    clearDrawingListeners()

    google.maps.event.addListener(root.$drawing, 'overlaycomplete', (event: any) => {
      const polygon = event.overlay as google.maps.Polygon
      polygon.setOptions({
        strokeColor: '#2d8cf0',
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: '#1791fc',
        fillOpacity: 0.4,
      })
      getDrawCallback(polygon)
    })
  }

  function drawCircle (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    root.$drawing.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE)
    clearDrawingListeners()

    google.maps.event.addListener(root.$drawing, 'overlaycomplete', (event: any) => {
      const circle = event.overlay as google.maps.Circle
      if (flightAreaType) {
        circle.setOptions({
          strokeColor: flightAreaColorMap[flightAreaType],
          strokeOpacity: 1,
          strokeWeight: 6,
          fillColor: flightAreaColorMap[flightAreaType],
          fillOpacity: flightAreaType === EFlightAreaType.NFZ ? 0.3 : 0,
        })
      }
      getDrawCallback(circle)
    })
  }

  function drawFlightAreaPolygon (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    root.$drawing.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
    clearDrawingListeners()

    google.maps.event.addListener(root.$drawing, 'overlaycomplete', (event: any) => {
      const polygon = event.overlay as google.maps.Polygon
      polygon.setOptions({
        strokeColor: flightAreaColorMap[type],
        strokeOpacity: 1,
        strokeWeight: 4,
        fillColor: flightAreaColorMap[type],
        fillOpacity: type === EFlightAreaType.NFZ ? 0.3 : 0,
      })
      getDrawFlightAreaCallback(polygon)
    })
  }

  function drawFlightAreaCircle (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    drawCircle(MapDoodleEnum.CIRCLE, getDrawFlightAreaCallback, type)
  }

  function drawOff () {
    root.$drawing.setDrawingMode(null)
    clearDrawingListeners()
  }

  function mouseTool (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    state.currentType = type

    if (flightAreaType) {
      switch (type) {
        case MapDoodleEnum.POLYGON:
          drawFlightAreaPolygon(flightAreaType, getDrawCallback)
          return
        case MapDoodleEnum.CIRCLE:
          drawFlightAreaCircle(flightAreaType, getDrawCallback)
          return
        default:
          message.error(`Invalid type: ${flightAreaType}`)
          return
      }
    }

    switch (type) {
      case MapDoodleEnum.PIN:
        drawPin(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYLINE:
        drawPolyline(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYGON:
        drawPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.CLOSE:
        drawOff()
        break
    }
  }

  return {
    mouseTool,
  }
}
